import { fail, ok, type Result } from '../result.js';

/**
 * Login por nome de usuário (val1, ficha `2026-10-28_malgo_FUN_login-por-usuario-e-email-varias-empresas.md`).
 *
 * O nome de usuário é da identidade global (único no sistema inteiro), sem "@" (o "@" separa o e-mail do usuário no campo
 * do login). Só ASCII minúsculo, dígitos, ponto, hífen e sublinhado, de 2 a 40 caracteres, começando e terminando por letra
 * ou dígito, sem dois separadores seguidos. Como é só ASCII, não há confundível de outro alfabeto (a regra de esqueleto dos
 * nomes de identidade, r10 a r14, existe para o Unicode; aqui ele é recusado de saída).
 *
 * A mesma regra está no banco (`nucleo_usuario_base` e o CHECK de `usuarios.usuario`, núcleo 0345); o teste
 * `login-usuario.int.test.ts` confere que a sugestão daqui e a do banco dão o mesmo resultado.
 */
const RE_FORMATO = /^[a-z0-9](?:[._-]?[a-z0-9])+$/;
export const NOME_USUARIO_MAX = 40;
/** Formato e tamanho (2 a 40) do nome de usuário. */
export const formatoNomeUsuario = (v: string) => v.length <= NOME_USUARIO_MAX && RE_FORMATO.test(v);

const ACENTOS: Record<string, string> = {
  á: 'a', à: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a',
  é: 'e', è: 'e', ê: 'e', ë: 'e',
  í: 'i', ì: 'i', î: 'i', ï: 'i',
  ó: 'o', ò: 'o', ô: 'o', õ: 'o', ö: 'o',
  ú: 'u', ù: 'u', û: 'u', ü: 'u',
  ç: 'c', ñ: 'n', ý: 'y', ÿ: 'y',
};

/** Só as letras com acento do português e do espanhol viram a letra sem acento; o resto fora do ASCII sai. */
function semAcento(s: string): string {
  return [...s.toLowerCase()].map((c) => ACENTOS[c] ?? c).join('');
}

/**
 * Sugestão `nome.sobrenome` a partir do nome da pessoa: minúsculo, sem acento, o primeiro e o último nome (cada um com até
 * 19 caracteres). Nome que não dá nada aproveitável (só símbolos, outro alfabeto, uma letra) vira `usuario`. A colisão
 * (`nome.sobrenome2`, 3...) é resolvida no banco, que é quem sabe o que já existe.
 */
export function sugerirNomeUsuario(nome: string): string {
  const palavras = semAcento(nome.normalize('NFC'))
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((p) => p.slice(0, 19));
  const base = palavras.length > 1 ? `${palavras[0]}.${palavras[palavras.length - 1]}` : (palavras[0] ?? '');
  return formatoNomeUsuario(base) ? base : 'usuario';
}

/** Normaliza o que a pessoa digitou (bordas e caixa) e confere a regra. Erro em `campo`. */
export function nomeUsuarioValido(bruto: unknown, campo = 'usuario'): Result<string> {
  const v = typeof bruto === 'string' ? bruto.trim().toLowerCase() : '';
  if (!v) return fail('usuario.obrigatorio', 'Informe o nome de usuário', { campo });
  if (v.includes('@')) return fail('usuario.invalido', 'O nome de usuário não pode ter "@"', { campo });
  if (!formatoNomeUsuario(v)) {
    return fail('usuario.invalido', 'Use de 2 a 40 letras sem acento, números, ponto, hífen ou sublinhado (sem começar ou terminar com eles)', { campo });
  }
  return ok(v);
}

/**
 * Conta sem e-mail (acesso por nome de usuário e senha provisória): o banco guarda um endereço técnico no domínio reservado
 * `.invalid` (RFC 2606), que nunca é entregue. O serviço de e-mail descarta o envio para ele e as telas mostram "sem e-mail".
 */
export const DOMINIO_SEM_EMAIL = 'sem-email.malgoos.invalid';
export function ehEmailTecnicoSemEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith('.invalid');
}

/**
 * Val7 (29/10, ficha `FUN_formas-de-acesso`): só duas formas de acesso, o e-mail de login e o nome de usuário; o convite leva
 * ao menos uma.
 */
export function conferirFormasAcesso(loginEmail: boolean, loginUsuario: boolean): Result<void> {
  return loginEmail || loginUsuario ? ok(undefined) : fail('acesso.forma_obrigatoria', 'Escolha ao menos uma forma de acesso', { campo: 'login_email' });
}

/**
 * Val7 (rval7 ROB-1, SPEC-1, SEG-B2): a base do nome de usuário reservado no convite: o escolhido, senão o `nome.sobrenome`
 * do nome do convite, senão a parte local do e-mail (convite sem nome). O banco reserva a variação livre a partir dela.
 */
export function baseUsuarioConvite(escolhido: string | null | undefined, nome: string, email: string): string {
  if (escolhido?.trim()) return escolhido.trim().toLowerCase();
  if (nome.trim()) return sugerirNomeUsuario(nome);
  return sugerirNomeUsuario((email.split('@')[0] ?? '').replace(/[._+-]+/g, ' '));
}

/**
 * Val7: os dados do e-mail do convite sobre as formas. Com o nome de usuário ativo, a mensagem leva o nome EFETIVO (o reservado
 * no convite, que o aceite usa exatamente, ou o da conta que já existe: rval7 ROB-1, ROB-2) e o link para a pessoa criar a
 * própria senha (nunca a senha no e-mail). Sem o e-mail de login, a mensagem não diz para entrar pelo e-mail.
 */
export function formasNoEmailConvite(loginEmail: boolean, loginUsuario: boolean, usuario: string | null): Record<string, unknown> {
  return { ...(loginUsuario && usuario ? { usuario } : {}), ...(loginEmail ? {} : { sem_login_email: true }) };
}
